import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Music24QuotaKind } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../../common/types';
import { formatQuotaReportingMonth, formatQuotaResetDate, quotaKindMessages, upgradeMessages } from './upgradeMessages';
import styles from './SMLUpgradeQuotaMeter.module.scss';

interface SMLUpgradeQuotaMeterProps {
  used: number;
  limit: number;
  kind: Music24QuotaKind;
  /** ISO time of the next monthly reset. `null` hides the reset date and the "in {month}" suffix. */
  resetsAt: string | null;
  /** The workspace's plan name, for the "Free plan" note under the bar. */
  currentPlanName: string;
}

/**
 * The row opens a sentence in design b2 ("Auto-posts in September"), so it is capitalised here —
 * the same `quotaKindMessages` entry stays lower-case mid-sentence in `SMLUpgradeOffer`'s title.
 */
function capitalize(text: string): string {
  return text.length === 0 ? text : `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

/** Design b2: the meter the `M24_LIMIT_QUOTA` prompt shows above the plans — full at the limit. */
const SMLUpgradeQuotaMeterComponent: React.FC<SMLUpgradeQuotaMeterProps> = ({
  used,
  limit,
  kind,
  resetsAt,
  currentPlanName,
}) => {
  const intl = useIntl();
  const percent = useMemo(() => (limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100), [used, limit]);
  const kindLabel = capitalize(intl.formatMessage(quotaKindMessages[kind]));
  const rowLabel =
    resetsAt === null
      ? kindLabel
      : intl.formatMessage(upgradeMessages.quotaKindInMonth, {
          kind: kindLabel,
          month: formatQuotaReportingMonth(intl, resetsAt),
        });

  return (
    <div className={styles.meter}>
      <div className={styles.row}>
        <span>{rowLabel}</span>
        <span className={styles.fig}>{intl.formatMessage(upgradeMessages.quotaFraction, { used, limit })}</span>
      </div>
      <div className={styles.bar} role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${percent}%` }} />
      </div>
      <div className={styles.row}>
        <span className={styles.muted}>
          {resetsAt === null
            ? null
            : intl.formatMessage(upgradeMessages.quotaResetsOn, { date: formatQuotaResetDate(intl, resetsAt) })}
        </span>
        <span className={styles.muted}>
          {intl.formatMessage(upgradeMessages.quotaCurrentPlan, { plan: currentPlanName })}
        </span>
      </div>
    </div>
  );
};

export const SMLUpgradeQuotaMeter = TypedMemo(SMLUpgradeQuotaMeterComponent);
